import { defineComponent as _defineComponent } from 'vue'
import { Ref, computed, onMounted, onUnmounted, ref, useSlots } from 'vue';
import CButton from '@/components/shared/CButton';
import CError from '@/components/shared/CError.vue';
import CPreview from '@/components/shared/CPreview.vue';
import { isValidFileSignature } from '@/components/shared/CFileInput/utils/fileSignatureValidation';
import { useToaster } from '@/composables/toaster';


export default /*#__PURE__*/_defineComponent({
  __name: 'CFileInput',
  props: {
    value: null,
    initialFilesSrc: { default: () => [] },
    allowSrcRemove: { type: Boolean },
    dragAndDrop: { type: Boolean },
    multiple: { type: Boolean },
    accept: { default: '*' },
    maxMbSize: { default: 10 },
    buttonText: null,
    previewButtonText: null,
    displayPreview: { type: Boolean, default: true },
    previewLabel: null,
    disabled: { type: Boolean },
    error: { type: Boolean },
    errorMsg: null
  },
  emits: ["input", "cancel", "src-preview-removed"],
  setup(__props: any, { expose, emit }: { emit: ({
    (event: 'input', value: File[]): void;
    (event: 'cancel'): void;
    (event: 'src-preview-removed', value: string[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: File[], initialFilesSrc: string[], allowSrcRemove?: boolean, dragAndDrop?: boolean, multiple?: boolean, accept: string, maxMbSize: number, buttonText?: string, previewButtonText?: string, displayPreview: boolean, previewLabel?: string, disabled?: boolean, error?: boolean, errorMsg?: string };





const slots = useSlots();
const toaster = useToaster();

const fileInputRef: Ref<HTMLInputElement | null> = ref(null);

const _initialSrcs = ref<string[]>(props.initialFilesSrc);

const files = computed({
    get: () => (props.value.length ? props.value : [..._initialSrcs.value]),
    set: (files: string[] | File[]): void => emit('input', files as File[])
});

const previewMode = computed(() => files.value.length);

const allowPreviewRemove = computed(
    () => !props.disabled && ((files.value.length !== 1 && !props.dragAndDrop) || props.allowSrcRemove)
);

const btnText = computed(() => (previewMode.value ? props.previewButtonText : props.buttonText));

const openFileUploader = () => fileInputRef.value?.click();

const handleFileChange = async (event: Event) => {
    const element = event.target as HTMLInputElement;
    await updateModel(element.files);
};

const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    if (!event.dataTransfer || props.disabled) return;
    event.dataTransfer.dropEffect = 'copy';
};

const handleDrop = async (event: DragEvent) => {
    event.preventDefault();
    if (!event.dataTransfer || props.disabled) return;

    await updateModel(event.dataTransfer.files, props.multiple);
};

const removeFile = (index?: number) => {
    clearInput();

    if (index == undefined) {
        files.value = [];
        return;
    }

    if (typeof files.value[index] == 'string') {
        _initialSrcs.value.splice(index, 1);
        emit('src-preview-removed', _initialSrcs.value);
        return;
    }

    files.value.splice(index, 1);
};

const clearInput = () => {
    if (!fileInputRef.value) return;
    fileInputRef.value.value = '';
};

const updateModel = async (list: FileList | null, concat?: boolean) => {
    const newFiles: File[] = [];
    let invalidSignatureCount = 0;

    /**
     * Use a forof to manually push each file to model array,
     * since Array.from and spread operator dont work properly
     * clonning a FileList
     */
    for (const file of list || []) {
        if (!(await isValidFileSignature(file, props.accept))) {
            invalidSignatureCount += 1;
            continue;
        }
        if (!props.multiple && newFiles.length) break;
        newFiles.push(file);
    }
    if (invalidSignatureCount) {
        const message =
            invalidSignatureCount === 1
                ? 'This file is not valid. Please try another one or contact support.'
                : 'Some files are not valid. Please try others or contact support.';
        toaster.error(message);
    }

    files.value = concat ? [...(files.value as File[]), ...newFiles] : newFiles;
};

const selectionCanceled = () => {
    clearInput();
    emit('cancel');
};

onMounted(() => {
    fileInputRef.value?.addEventListener('cancel', selectionCanceled);
});

onUnmounted(() => {
    fileInputRef.value?.removeEventListener('cancel', selectionCanceled);
});

expose({ openFileUploader });

return { __sfc: true,props, emit, slots, toaster, fileInputRef, _initialSrcs, files, previewMode, allowPreviewRemove, btnText, openFileUploader, handleFileChange, handleDragOver, handleDrop, removeFile, clearInput, updateModel, selectionCanceled, CButton, CError, CPreview }
}

})